import { Fragment, memo, useRef } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { PageControlDotSelected, User, UserGroup } from '@sas/react-svg';
import cx from 'classnames';
import { useTruncationTooltip } from '../../../../hooks/useTruncationTooltip';
import { useFormatLocale } from '../../../../locale/formatting';
import { renderDesignTokens, useDesignTokens } from '../../../../theme';
import { postfixTestId } from '../../../../util/testId';
import { Avatar } from '../../../Avatar';
import { Icon } from '../../../Icon';
import { useIconA11yInfo } from '../../../Icon/useIconA11yInfo';
import { Flow } from '../../../Layouts/Flow';
import { Text } from '../../../Text';
import '../Item.css';
import cn from '../Item.css.json';

/**
 * @internal
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const BaseItem = /*#__PURE__*/memo(props => {
  const refDiv = useRef(null);
  const dt = useDesignTokens();
  const tokens = {
    ...getListItemDesignTokens(dt, {
      tokens: props.tokens,
      concatInfo: props?.info?.concatInfo
    }),
    ...props.theme?.(dt)
  };
  const getIconA11yInfo = useIconA11yInfo();
  const itemContent = /*#__PURE__*/_jsxs("div", {
    style: {
      ...tokens,
      ...props.style
    },
    className: cx(cn.item, props.className, props.disabled && cn.disabled, props.unread === false && cn.read, props.unread === true && cn.unread, props.info?.hasPriority && cn.infoHasPriority),
    ref: refDiv,
    "data-testid": props.testId,
    dir: props.dir,
    children: [props.unread === true ? /*#__PURE__*/_jsx(Icon, {
      icon: PageControlDotSelected,
      theme: () => {
        return {
          '--icon-default-override-fg': 'var(--list-item-unread-bg)'
        };
      },
      className: cn.dot,
      style: {
        pointerEvents: 'none'
      }
    }) : null, /*#__PURE__*/_jsxs(Flow, {
      spacing: "s",
      className: cx(cn.contentContainer, props.icon && cn.hasItemIcon, props.avatar && cn.hasItemAvatar),
      children: [props.icon ? ItemIcon(props) : ItemAvatar(props, getIconA11yInfo), props.description ? DoubleLines(props) : SingleLine(props)]
    }), props.description && props.info && !props.info.concatInfo ? InfoText(props) : undefined, isNullish(props.counter) ? null : /*#__PURE__*/_jsx(Counter, {
      ...props
    })]
  });
  return itemContent;
});
export const getListItemDesignTokens = (dt, opts) => {
  return {
    ...renderDesignTokens({
      fg: opts?.tokens?.fg,
      'font-family': opts?.tokens?.fontFamily,
      'font-size': opts?.tokens?.fontSize,
      'font-weight': opts?.tokens?.fontWeight,
      'font-style': opts?.tokens?.fontStyle,
      'text-decoration': opts?.tokens?.textDecoration,
      gap: opts?.concatInfo ? '' : dt.tokens['space-inline-s']
    }, 'list-item')
  };
};
const SingleLine = props => {
  return /*#__PURE__*/_jsxs("div", {
    className: cn.singleLine,
    children: [ItemText(props), InfoText(props)]
  });
};
const DoubleLines = props => {
  return /*#__PURE__*/_jsxs("div", {
    className: cn.doubleLines,
    children: [ItemText(props), SubSection(props)]
  });
};
const SubSection = props => {
  return /*#__PURE__*/_jsxs("span", {
    className: cn.subSection,
    children: [/*#__PURE__*/_jsx(Text, {
      testId: postfixTestId(props.testId, 'description'),
      className: cx(cn.itemDescriptionText, props.unread === false && cn.read, props.unread === true && cn.unread),
      disabled: props.disabled,
      highlightableConfig: {
        highlight: !props.highlight?.excludeDescription ? props.highlight?.text : undefined
      },
      wrapText: false,
      children: props.description ?? ''
    }), props.info?.concatInfo ? InfoText(props) : undefined]
  });
};
const ItemText = props => {
  const setTruncationRef = useTruncationTooltip({
    tooltip: props.tooltip
  });
  const wrap = !isNullish(props.titleMaxLines);
  return /*#__PURE__*/_jsx(Text, {
    id: props.id,
    className: cx(cn.itemText, wrap && cn.wrap, props.description && [cn.bold, cn.description], props.mixedLines && [cn.bold], props.unread === false && cn.read, props.unread === true && cn.unread),
    testId: postfixTestId(props.testId, 'title'),
    disabled: props.disabled,
    highlightableConfig: {
      highlight: !props.highlight?.excludeTitle ? props.highlight?.text : undefined
    },
    ref: setTruncationRef,
    wrapText: wrap,
    maxLines: props.titleMaxLines,
    dir: props.dir,
    children: props.title ?? ''
  });
};
const InfoText = props => {
  if (!props.info) {
    return null;
  }
  const hyphen = props.info.concatInfo ?
  /*#__PURE__*/
  // eslint-disable-next-line @sas/i18n/jsx-to-localize
  _jsx(Text, {
    className: cx(cn.itemTextSeparator, props.unread === false && cn.read, props.unread === true && cn.unread),
    disabled: props.disabled,
    dir: props.dir,
    children: "-"
  }) : null;
  const secondaryText = /*#__PURE__*/_jsx(Text, {
    className: cx(cn.itemInfoText, props.info.concatInfo && cn.concat, props.unread === false && cn.read, props.unread === true && cn.unread),
    testId: postfixTestId(props.testId, 'info'),
    disabled: props.disabled,
    highlightableConfig: {
      highlight: !props.highlight?.excludeInfo ? props.highlight?.text : undefined
    },
    wrapText: false,
    dir: props.dir,
    children: props.info.infoText ?? ''
  });
  return props.info.concatInfo ? /*#__PURE__*/_jsxs(Fragment, {
    children: [hyphen, secondaryText]
  }) : /*#__PURE__*/_jsx(Fragment, {
    children: secondaryText
  });
};
const Counter = props => {
  const locale = useFormatLocale();
  if (isNullish(props.counter)) {
    return null;
  }
  const numberFormat = new Intl.NumberFormat(locale);
  return /*#__PURE__*/_jsx(Text, {
    "data-testid": postfixTestId(props.testId, 'counter'),
    className: cx(cn.counter, props.unread === false && cn.read, props.unread === true && cn.unread),
    disabled: props.disabled,
    highlightableConfig: {
      highlight: !props.highlight?.excludeCounter ? props.highlight?.text : undefined
    },
    wrapText: false,
    children: `(${numberFormat.format(props.counter)})`
  });
};
const ItemIcon = props => {
  if (isNullish(props.icon)) {
    return null;
  }
  return /*#__PURE__*/_jsx(Icon, {
    ...props.icon,
    className: cx(cn.itemIcon, props.icon.className),
    theme: () => {
      props.icon?.theme ? props.icon.theme : () => ({
        '--icon-default-override-fg': 'var(--list-item-fg)'
      });
    },
    disabled: props.disabled
  });
};
const ItemAvatar = (props, getIconA11yInfo) => {
  const {
    avatar
  } = props;
  if (isNullish(avatar)) {
    return null;
  }
  return /*#__PURE__*/_jsx(Avatar, {
    ...avatar,
    size: "small",
    ariaLabel: avatar.ariaLabel ?? '',
    tooltip: avatar.tooltip ?? getIconA11yInfo({
      icon: avatar.type === 'user' ? User : UserGroup,
      // the following testId is required for testing to get some tooltip
      // to work with (ends up SASDesign-Icon-gui-icu.User.icon.label or
      // SASDesign-Icon-gui-icu.UserGroup.icon.label)
      testId: postfixTestId(props.testId, 'icon')
    }).tooltip,
    className: cx(cn.itemAvatar, avatar.className)
  });
};
//# sourceMappingURL=BaseItem.js.map